import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useOnboardingStore } from '@/hooks/use-onboarding-store';
import { supabase } from '@/integrations/supabase/client';

interface Step8EmailProps {
  onNext: () => void;
}

const Step8Email: React.FC<Step8EmailProps> = ({ onNext }) => {
  const { email, phone, setEmail, setPhone, answers } = useOnboardingStore();
  const [error, setError] = useState('');
  const [inputValue, setInputValue] = useState(email || '');
  const [phoneValue, setPhoneValue] = useState(phone || '');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    return phoneRegex.test(phone.trim());
  };

  const handleSubmit = async () => {
    if (!inputValue.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(inputValue)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!phoneValue.trim()) {
      setError('Please enter your phone number');
      return;
    }

    if (!validatePhone(phoneValue)) {
      setError('Please enter a valid phone number');
      return;
    }

    // Save lead data to database
    try {
      const { error } = await supabase
        .from('leads')
        .insert({
          email: inputValue,
          phone: phoneValue,
          onboarding_answers: answers,
          step_reached: 8
        });

      if (error) {
        console.error('Error saving lead:', error);
        // Continue with the flow even if lead saving fails
      }
    } catch (error) {
      console.error('Error saving lead:', error);
      // Continue with the flow even if lead saving fails
    }

    setError('');
    setEmail(inputValue);
    setPhone(phoneValue);
    onNext();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (error) setError('');
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneValue(e.target.value);
    if (error) setError('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="p-6 max-w-lg mx-auto text-center"
    >
      {/* Icon */}
      <div className="mb-6">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-nocode-blue to-nocode-purple rounded-full flex items-center justify-center">
          <Mail className="h-8 w-8 text-white" />
        </div>
      </div>

      {/* Headlines */}
      <h2 className="text-3xl font-bold text-gray-900 mb-4">
        Unlock Your Personalized{' '}
        <span className="text-nocode-blue">AI Building Blueprint!</span>
      </h2>

      <p className="text-gray-600 text-lg mb-8 leading-relaxed">
        Enter your contact details to receive your tailored plan for building, launching, and owning your digital product with AI, plus exclusive insights and tips.
      </p>

      {/* Contact Inputs */}
      <div className="mb-6 space-y-4">
        {/* Email Input */}
        <div className="relative">
          <Input
            type="email"
            placeholder="Type your email"
            value={inputValue}
            onChange={handleInputChange}
            className={`pl-12 pr-4 py-3 text-lg rounded-xl border-2 ${
              error ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-nocode-blue'
            } transition-colors`}
          />
          <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>

        {/* Phone Input */}
        <div className="relative">
          <Input
            type="tel"
            placeholder="Type your phone number"
            value={phoneValue}
            onChange={handlePhoneChange}
            className={`pl-12 pr-4 py-3 text-lg rounded-xl border-2 ${
              error ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-nocode-blue'
            } transition-colors`}
          />
          <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>

        {error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-500 text-sm mt-2 text-left"
          >
            {error}
          </motion.p>
        )}
      </div>

      {/* Continue Button */}
      <Button
        onClick={handleSubmit}
        size="lg"
        className="w-full bg-gradient-to-r from-nocode-blue to-nocode-purple text-white hover:opacity-90 font-semibold py-3 text-lg rounded-xl transition-all duration-300 mb-6"
      >
        Continue
      </Button>

      {/* Disclaimer */}
      <p className="text-sm text-gray-500 leading-relaxed">
        By clicking continue, you agree to iSelect Digital's{' '}
        <a href="#" className="text-nocode-blue hover:underline">
          Terms & Conditions
        </a>{' '}
        and{' '}
        <a href="#" className="text-nocode-blue hover:underline">
          Privacy Policy
        </a>
        .
      </p>
    </motion.div>
  );
};

export default Step8Email;